import type { HTMLAttributes, TdHTMLAttributes, ThHTMLAttributes } from 'react'
import { cn } from '../lib/cn'

/**
 * Tabela — componente 05.
 * Cabeçalho é rótulo espaçado sobre pampas — nunca faixa colorida. Sem zebra; separação por fio.
 * Hover pinta a linha em pampas e revela as ações em texto à direita (`RowActions`); nenhum ícone
 * de lixeira fica visível em repouso. Ordenação só nas colunas que fazem sentido, seta apenas na ativa.
 * Linha crítica ganha fio de 2px à esquerda (`critical`). Abaixo de 900px cada linha vira cartão.
 */

export function Table({ className, ...props }: HTMLAttributes<HTMLTableElement>) {
  return (
    <div data-slot="table-wrap" className="border-hairline bg-surface border">
      <table
        data-slot="table"
        className={cn(
          'max-desk:block [&_tbody]:max-desk:block [&_tr]:max-desk:block w-full border-collapse text-left',
          className,
        )}
        {...props}
      />
    </div>
  )
}

export function TableHeader({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <thead
      data-slot="table-header"
      className={cn('bg-pampas max-desk:hidden', className)}
      {...props}
    />
  )
}

export function TableBody({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody data-slot="table-body" className={className} {...props} />
}

export type SortDirection = 'asc' | 'desc'

export interface TableHeadProps extends ThHTMLAttributes<HTMLTableCellElement> {
  /** Direção quando esta é a coluna ativa de ordenação; a seta só aparece aqui. */
  sorted?: SortDirection | null
  /** Coluna ordenável: vira botão. Só nas colunas onde ordenar faz sentido. */
  onSort?: () => void
  numeric?: boolean
}

export function TableHead({
  className,
  sorted,
  onSort,
  numeric,
  children,
  ...props
}: TableHeadProps) {
  const content = (
    <>
      {children}
      {sorted ? <span aria-hidden> {sorted === 'asc' ? '▲' : '▼'}</span> : null}
    </>
  )
  return (
    <th
      scope="col"
      data-slot="table-head"
      aria-sort={sorted ? (sorted === 'asc' ? 'ascending' : 'descending') : undefined}
      className={cn(
        'eyebrow border-hairline border-b px-4 py-3 align-middle font-light',
        numeric && 'text-right',
        className,
      )}
      {...props}
    >
      {onSort ? (
        <button type="button" onClick={onSort} className="eyebrow hover:text-ink cursor-pointer">
          {content}
        </button>
      ) : (
        content
      )}
    </th>
  )
}

export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  /** Linha crítica: fio plum de 2px à esquerda. Nada de fundo colorido. */
  critical?: boolean
  /** Linha selecionada em lote. */
  selected?: boolean
}

export function TableRow({ className, critical, selected, onClick, ...props }: TableRowProps) {
  return (
    <tr
      data-slot="table-row"
      data-critical={critical || undefined}
      data-selected={selected || undefined}
      onClick={onClick}
      className={cn(
        'group border-hairline/60 border-b transition-[background-color] last:border-b-0',
        'max-desk:relative max-desk:py-3',
        onClick && 'hover:bg-pampas focus-within:bg-pampas cursor-pointer',
        critical && 'shadow-[inset_2px_0_0_var(--color-plum)]',
        selected && 'bg-pampas',
        className,
      )}
      {...props}
    />
  )
}

export interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  /** Texto secundário (#565060). */
  muted?: boolean
  /** Número alinhado à direita com dígitos tabulares. */
  numeric?: boolean
  /** Nome em Lexend 14px — a primeira coluna de uma lista de pessoas. */
  primary?: boolean
  /** Rótulo mostrado antes do valor quando a linha vira cartão (<900px). */
  label?: string
}

export function TableCell({
  className,
  muted,
  numeric,
  primary,
  label,
  children,
  ...props
}: TableCellProps) {
  return (
    <td
      data-slot="table-cell"
      data-label={label}
      className={cn(
        'font-body text-ink px-4 py-[14px] align-middle text-[13px] leading-[1.4] font-light',
        'max-desk:flex max-desk:items-baseline max-desk:gap-3 max-desk:py-1 max-desk:first:pt-4 max-desk:last:pb-4',
        label &&
          'max-desk:before:eyebrow max-desk:before:min-w-[88px] max-desk:before:content-[attr(data-label)]',
        muted && 'text-secondary',
        numeric && 'max-desk:text-left text-right tabular-nums',
        primary && 'font-display text-lead max-desk:text-name leading-[1.35] font-normal',
        className,
      )}
      {...props}
    >
      {children}
    </td>
  )
}

/**
 * Ações em texto, reveladas no hover ou foco da linha. Sempre visíveis no celular.
 * Coloque `<TableCell>` com `RowActions` como última célula.
 */
export function RowActions({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="row-actions"
      onClick={(e) => e.stopPropagation()}
      className={cn(
        'font-body text-eyebrow text-label max-desk:justify-start max-desk:opacity-100 [&>button]:hover:text-ink [&>a]:hover:text-ink flex items-center justify-end gap-4 font-light opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 [&>button]:cursor-pointer',
        // No cartão as ações ficam sempre à vista; a área de toque de 48px vem de um pseudo-elemento.
        "max-desk:[&>*]:relative max-desk:[&>*]:after:absolute max-desk:[&>*]:after:inset-x-0 max-desk:[&>*]:after:top-1/2 max-desk:[&>*]:after:h-touch max-desk:[&>*]:after:-translate-y-1/2 max-desk:[&>*]:after:content-['']",
        className,
      )}
      {...props}
    />
  )
}

/** Rodapé da lista: contagem, critério de ordenação, explicação — a camada que sustenta. */
export function TableFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="table-footer"
      className={cn(
        'font-body text-eyebrow text-label flex flex-wrap items-center justify-between gap-3 px-4 py-3 leading-[1.6] font-light tracking-normal',
        className,
      )}
      {...props}
    />
  )
}
